import { deriveIdvRange, getActiveIdvFormula, midpointIdv, resolveIdvRange } from "./idvHelpers";

// ── The IDV on the Online Policy form: selling price -> IDV (the owner, 2026-10-27) ─────────────────────────────────
//
// The Manufacturer Selling Price is shown and editable on the form (and in the IDV Calculator beside the IDV): a price
// works the IDV out (price less the vehicle's depreciation) and the IDV can still be typed over. The IDV RANGE is the
// Make & Model's only - it is never changed on a policy - and an IDV outside it is refused (the owner: "range only the
// make and model allows, and only give the IDV in that range"). A selling price that differs from the master marks the
// policy `idvByHand` (a blue note says so; the master itself is never changed). Pure helpers; the dialog is
// components/PolicyIdvCalculatorDialog.js.

/** The saved formula's name (FORMULA_NAMES.IDV_RANGE in Service/SearchPolicy.js - not imported, it pulls axios into tests). */
export const IDV_FORMULA_NAME = "IDV_RANGE";

const text = (v) => (v === undefined || v === null ? "" : String(v).trim());
const digits = (v) => text(v).replace(/[^0-9.]/g, "");
const num = (v) => {
  const t = digits(v);
  if (!t) return NaN;
  const n = Number(t);
  return Number.isFinite(n) ? n : NaN;
};
const same = (a, b) => {
  const x = num(a);
  const y = num(b);
  return Number.isFinite(x) && Number.isFinite(y) ? x === y : text(a) === text(b);
};

/** ₹ 1,16,850 */
export const rupees = (v) => {
  const n = num(v);
  return Number.isFinite(n) ? `₹${n.toLocaleString("en-IN")}` : "Not set";
};

/** The master's figures for a vehicle: its selling price and the IDV range allowed for it (resolveIdvRange). */
export const masterIdvOf = (record) => {
  const range = resolveIdvRange(record || {});
  return { price: text(record && record.manufacturerSellingPrice), from: range.from, to: range.to };
};

/** The vehicle's depreciation %, else the saved formula's. */
const depreciationOf = (record, formula = getActiveIdvFormula()) =>
  text(record && record.depreciationPercent) !== "" ? text(record.depreciationPercent) : String(formula.depreciationPercent);

/** The IDV a selling price gives: the price less the depreciation ("" when the price is unusable). */
export const idvFromPrice = (price, depreciationPercent) => deriveIdvRange(digits(price), undefined, depreciationPercent).idv;

/**
 * What the calculator opens with. `range` is the range in force on the form (the Make & Model's, or the one an edited
 * policy was saved with); it is shown, never edited.
 */
export const initialCalc = ({ record, values = {}, range, formula = getActiveIdvFormula() } = {}) => {
  const master = masterIdvOf(record);
  const allowed = range && (range.from || range.to) ? { from: text(range.from), to: text(range.to) } : { from: master.from, to: master.to };
  return {
    price: digits(values.manufacturerSellingPrice) || digits(master.price),
    dep: depreciationOf(record, formula),
    from: allowed.from,
    to: allowed.to,
    idv: num(values.idv) > 0 ? text(values.idv) : midpointIdv(allowed.from, allowed.to),
  };
};

/** The master's own price and the middle IDV of the allowed range, for "Use master values". */
export const masterCalc = (record, range, formula) => {
  const start = initialCalc({ record, values: {}, range, formula });
  return { ...start, idv: midpointIdv(start.from, start.to) };
};

/** The price or the depreciation changed: the IDV follows (the range does not - it is the Make & Model's). */
export const recalc = (state, patch) => {
  const next = { ...state, ...patch };
  const idv = idvFromPrice(next.price, next.dep);
  return idv ? { ...next, idv } : next;
};

/** What is wrong with the IDV, in plain words, or "" (it may be used). */
export const calcProblem = ({ from, to, idv } = {}) => {
  const f = num(from);
  const t = num(to);
  const i = num(idv);
  if (!(i > 0)) return "Enter the IDV for this policy.";
  if (f > 0 && t > 0 && (i < Math.min(f, t) || i > Math.max(f, t))) {
    return `The IDV must be inside the Make & Model range (${rupees(Math.min(f, t))} to ${rupees(Math.max(f, t))}).`;
  }
  return "";
};

/**
 * Where the policy differs from the master - the rows of the "set by hand" note: [{ key, label, master, policy }].
 * Only the selling price can differ (the range is always the Make & Model's). A price cleared on the form while the
 * master has one counts too, or the master's would be put straight back while the operator is still retyping it.
 */
export const byHandChanges = ({ record, values = {} } = {}) => {
  const master = masterIdvOf(record);
  const price = digits(values.manufacturerSellingPrice);
  if ((price || master.price) && !same(price, master.price)) {
    return [{ key: "price", label: "Selling price", master: rupees(master.price), policy: rupees(price) }];
  }
  return [];
};

/**
 * The Manufacturer Selling Price typed on the form: the IDV is worked out from it (unless the insurer works the IDV out
 * itself), the range stays the Make & Model's. Returns the form values to write.
 */
export const fromSellingPrice = (price, { record, keepIdv = false } = {}) => {
  const typed = digits(price);
  const next = { manufacturerSellingPrice: typed };
  const idv = idvFromPrice(typed, depreciationOf(record));
  if (idv && !keepIdv) next.idv = idv;
  next.idvByHand = byHandChanges({ record, values: next }).length > 0;
  return next;
};

/** The form values the calculator's Update writes (the range is not among them). */
export const appliedValues = (state, record) => {
  const values = { idv: text(state.idv), manufacturerSellingPrice: digits(state.price) };
  return { ...values, idvByHand: byHandChanges({ record, values }).length > 0 };
};
